/**
 * Records every destructive ("red") toast into `error_logs`.
 *
 * WHY: a failure the user sees is the one that matters, and until now it lived
 * only on their screen. The invoice-send 401 (gigpig #889) surfaced as
 * "GIG-0423: Unauthorized" in a toast and nowhere else — it took pasting the
 * text and reading Supabase edge logs to find it. Anything a user can see fail
 * should be visible to us without them having to report it.
 *
 * Hooked at the toast dispatcher rather than at call sites, so it catches every
 * red notification platform-wide, including ones written after this.
 *
 * Design constraints, all deliberate:
 * - NEVER throws and never blocks the toast. Logging a failure must not become
 *   a second failure; the user still gets their message either way.
 * - Fire-and-forget. No await in the render path.
 * - Deduped over a short window. A bulk action that fails 20 rows raises 20
 *   toasts; that is one incident, not twenty, and hammering the table on a
 *   flapping network is how a logger becomes the outage.
 * - Best-effort user id. An unauthenticated failure is still worth recording.
 */
import { supabase } from '@/integrations/supabase/client';

/** Recently logged signatures -> timestamp, for suppression. */
const recent = new Map<string, number>();
const DEDUPE_MS = 30_000;
/** Hard ceiling per page load, so a render loop can't flood the table. */
const MAX_PER_SESSION = 50;
let logged = 0;

function pruneRecent(now: number) {
  for (const [k, t] of recent) {
    if (now - t > DEDUPE_MS) recent.delete(k);
  }
}

function asText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  // Toast titles/descriptions are often ReactNode; take what is readable.
  if (typeof v === 'object' && 'props' in (v as Record<string, unknown>)) {
    const p = (v as { props?: { children?: unknown } }).props;
    if (typeof p?.children === 'string') return p.children;
  }
  return String(v);
}

export function captureDestructiveToast(input: {
  title?: unknown;
  description?: unknown;
}): void {
  try {
    if (logged >= MAX_PER_SESSION) return;

    const title = asText(input.title).trim();
    const description = asText(input.description).trim();
    const message = [title, description].filter(Boolean).join(' — ') || 'Unlabelled error toast';

    const now = Date.now();
    pruneRecent(now);
    const signature = `${message}|${window.location.pathname}`;
    if (recent.has(signature)) return;
    recent.set(signature, now);
    logged += 1;

    // Fire-and-forget: resolve the user, then insert. Any failure is swallowed.
    void (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        await supabase.from('error_logs').insert({
          severity: 'medium',
          category: 'ui_error', // error_logs CHECK allows 'ui_error'; 'ui_error_toast' 400'd every capture
          message: message.slice(0, 2000),
          user_id: data?.user?.id ?? null,
          user_agent: navigator.userAgent,
          url: window.location.href,
          component: 'toast',
          action: title.slice(0, 200) || null,
          metadata: {
            title: title.slice(0, 500),
            description: description.slice(0, 2000),
            pathname: window.location.pathname,
          },
        });
      } catch {
        // Deliberately silent — see the module note.
      }
    })();
  } catch {
    // Capture must never affect the toast that triggered it.
  }
}
